import DataSource from "devextreme/data/data_source";
import {loadFn} from "helpers/custom-data-source";
import {client} from "helpers/client";
import React from "react";

import PaidIcon from "assets/images/svg/paid_invoice.svg";
import UnpaidIcon from "assets/images/svg/unpaid_invoice.svg";
import CanceledIcon from "assets/images/svg/canceled_invoice.svg";
import RefundedIcon from "assets/images/svg/refunded_invoice.svg";
import PaidIconAr from 'assets/images/png/paid_ar.png';
import UnpaidIconAr from 'assets/images/png/unpaid_ar.png';
import CanceledIconAr from 'assets/images/png/canceled_ar.png';
import RefundedIconAr from 'assets/images/png/refunded_ar.png';
import CashIcon from "assets/images/svg/cash_green.svg";
import WaitingCashIcon from "assets/images/svg/waiting_dark.svg";
import FawryIcon from "assets/images/svg/fawry_intable.svg";
import _ from "lodash";
import PaypalIcon from "assets/images/svg/paypal_intable.svg";
import VisaIcon from "assets/images/svg/visa_intable.svg";
import MasterIcon from "assets/images/svg/master_intable.svg";
import {toast} from "react-toastify";
import {t} from "i18next";

interface PaymentInfo {
    bank: { cardNumber: string; cardTypeId: number };
    fawry: { referenceNumber: string, cardNumber: string, isFawryCard: boolean };
    paypal: { account: string };
}

export enum PaymentType {
    Paypal = 1,
    Fawry = 2,
    Master = 3,
    WaitCashRefund = 4,
    Cash = 5
}

export enum BankcardType {
    MasterCard = 1,
    Visa = 2
}

export interface Invoice {
    id: string;
    customer: {
        name: string;
        account: string;
        taxReg: string;
        address: string;
    };
    company: { name: string; account: string; taxReg: string; address: string };
    priceLevel: {
        id: number;
        isActive: boolean;
        name: string;
        numberOfLicenses: number;
    };
    subscription: {
        id: number;
        name: string;
    };
    expirationDate: string;
    serial: string;
    createDate: string;
    startDate: string;
    endDate: string;
    paymentTypeId: string;
    invoiceStatus: string;
    invoiceStatusId: string;
    paymentDate: string;
    productTitle: string;
    invoiceTitle: string;
    paymentMethodId: number;
    paymentMethod: string;
    paymentInfo: Partial<PaymentInfo>;
    notes: string;
    vatPercentage: number;
    totalVatAmount: number;
    discount: number;
    subTotal: number;
    total: number;
    currency: string;
    discriminator: number
}

export enum InvoiceStatus {
    Unpaid = 1,
    Paid = 2,
    RefundPending = 3,
    Refunded = 4,
    Cancelled = 5
}

export const getInvoiceDataSource = (invoiceStatus: InvoiceStatus) =>
    new DataSource({
        key: "id",
        load: loadFn((p) => client("/invoice/getPagedList", {params: {...p, InvoiceStatusId: invoiceStatus}}))
    });

export async function downloadInvoicePdf(invoice: Invoice) {
    const {response} = await client<any>('/invoice/DownloadInvoices', {data: [invoice.id]})

    if (response) {
        const a = document.createElement('a');
        a.href = response.fileBase64;
        a.download = response.fileName;
        a.click();
    }
}

export async function download(invoices: number[], cb?: (error?: any) => void) {
    if (!invoices.length) return toast.error(t('common:form.pleaseSelect')as string);
    const {response, error} = await client<any>('/invoice/DownloadInvoices', {
        data: invoices,
    });
    if (response) {
        const a = document.createElement('a');
        a.href = response.fileBase64;
        a.download = response.fileName;
        a.click();
        cb?.()
        return toast.success(t('common:form.invoiceDownload')as string)
    }
    if (error) return cb?.(error);
}

export const InvoiceStatusIcons: any = {
    [InvoiceStatus.Paid]: PaidIcon,
    [InvoiceStatus.Unpaid]: UnpaidIcon,
    [InvoiceStatus.Cancelled]: CanceledIcon,
    [InvoiceStatus.Refunded]: RefundedIcon
};

export const InvoiceStatusIconsAr: any = {
    [InvoiceStatus.Paid]: PaidIconAr,
    [InvoiceStatus.Unpaid]: UnpaidIconAr,
    [InvoiceStatus.Cancelled]: CanceledIconAr,
    [InvoiceStatus.Refunded]: RefundedIconAr,
};

export const isPaid = (invoice: Invoice) => invoice.invoiceStatusId === InvoiceStatus.Paid.toString();
export const isRefunded = (invoice: Invoice) => invoice.invoiceStatusId === InvoiceStatus.Refunded.toString();
export const isCanceled = (invoice: Invoice) => invoice.invoiceStatusId === InvoiceStatus.Cancelled.toString();
export const isUnpaid = (invoice: Invoice) => invoice.invoiceStatusId === InvoiceStatus.Unpaid.toString();

export const getInvoicePaymentMethod = (invoice: Invoice) => {
    switch (parseInt(invoice.paymentTypeId)) {
        case PaymentType.Fawry:
            let cardInfo = invoice.paymentInfo?.fawry?.isFawryCard ? invoice.paymentInfo?.fawry?.cardNumber : invoice.paymentInfo?.fawry?.referenceNumber
            return {
                icon: FawryIcon,
                info: _.capitalize(invoice.paymentMethod) + " - " + cardInfo
            };
        case PaymentType.Paypal:
            return {
                icon: PaypalIcon,
                info: _.capitalize(invoice.paymentMethod) + " - " + invoice.paymentInfo?.paypal?.account
            };
        case PaymentType.Master:
            return {
                icon: invoice.paymentInfo.bank?.cardTypeId === BankcardType.Visa ? VisaIcon : MasterIcon,
                info: _.capitalize(invoice.paymentMethod) + " - " + invoice.paymentInfo?.bank?.cardNumber
            };
        case PaymentType.Cash:
            return {
                icon: CashIcon,
                info: "Cash"
            }
        case PaymentType.WaitCashRefund:
            return {
                icon: WaitingCashIcon,
                info: "Wait Cash Refund"
            }
    }
};
